<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  products: { type: Array, default: () => [] },
  productName: { type: String, default: '' },
  availableProducts: { type: Array, default: () => [] },
  isPending: { type: Boolean, default: false },
  hasError: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits([
  'update:productName',
  'addProduct',
  'removeProduct',
  'updateProductValue',
  'retry',
]);

const { t } = useI18n();
const catalogSearch = ref('');
const normalizeSearch = text =>
  text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
const filteredProducts = computed(() =>
  props.availableProducts.filter(
    product =>
      !props.products.some(item => item.product_id === product.id) &&
      normalizeSearch(
        `${product.name} ${product.external_data?.sku || ''}`
      ).includes(normalizeSearch(catalogSearch.value.trim()))
  )
);
const currency = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});
const priceLabel = product => {
  const min =
    product.min_price == null ? 'R$ —' : currency.format(product.min_price);
  const max =
    product.max_price == null ? 'R$ —' : currency.format(product.max_price);
  return min === max ? min : `${min} – ${max}`;
};
const numberValue = event =>
  event.target.value === '' ? '' : Number(event.target.value);
</script>

<template>
  <div class="flex flex-col gap-1.5">
    <span class="text-xs font-medium text-n-slate-9">
      {{ t('KANBAN.PRODUCT_LABEL') }}
    </span>
    <input
      v-if="!products.length"
      :value="productName"
      :aria-label="t('KANBAN.PRODUCT_LABEL')"
      type="text"
      :disabled="disabled"
      :placeholder="t('KANBAN.PRODUCT_PLACEHOLDER')"
      class="w-full px-2.5 py-1.5 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 outline-none focus:ring-2 focus:ring-n-brand"
      @input="emit('update:productName', $event.target.value)"
    />
    <div
      v-for="(product, index) in products"
      :key="product.product_id"
      class="flex items-center justify-between px-3 py-2 rounded-lg border border-n-weak bg-n-alpha-1"
    >
      <span class="text-xs font-medium text-n-slate-12">
        {{ product.name }}
      </span>
      <div class="flex items-center gap-1.5">
        <input
          :value="product.value"
          :data-test-id="`deal-product-value-${product.product_id}`"
          type="number"
          min="0"
          step="0.01"
          :disabled="disabled"
          class="w-20 px-1.5 py-0.5 text-xs rounded border border-n-weak bg-n-background text-n-slate-12 outline-none focus:ring-1 focus:ring-n-brand text-right"
          @input="
            emit('updateProductValue', {
              index,
              value: numberValue($event),
            })
          "
        />
        <button
          type="button"
          :disabled="disabled"
          :aria-label="t('KANBAN.REMOVE_PRODUCT')"
          class="p-0.5 text-n-slate-10 hover:text-n-danger"
          @click="emit('removeProduct', index)"
        >
          <span class="i-lucide-x size-3 block" />
        </button>
      </div>
    </div>
    <p v-if="hasError" role="alert" class="text-xs text-n-ruby-11">
      {{ t('KANBAN.CATALOG_ERROR') }}
      <Button
        :label="t('KANBAN.CATALOG_RETRY')"
        size="xs"
        :disabled="disabled"
        @click="emit('retry')"
      />
    </p>
    <p
      v-else-if="!isPending && !availableProducts.length"
      class="text-xs text-n-slate-11"
    >
      {{ t('KANBAN.CATALOG_EMPTY') }}
    </p>
    <input
      v-if="availableProducts.length"
      v-model="catalogSearch"
      type="search"
      data-test-id="catalog-search"
      :disabled="disabled"
      :aria-label="t('KANBAN.CATALOG_SEARCH')"
      :placeholder="t('KANBAN.CATALOG_SEARCH')"
      class="w-full px-2.5 py-1.5 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 outline-none focus:ring-2 focus:ring-n-brand"
    />
    <p
      v-if="catalogSearch && !filteredProducts.length"
      role="status"
      class="text-xs text-n-slate-11"
    >
      {{ t('KANBAN.CATALOG_NO_MATCHES') }}
    </p>
    <select
      data-test-id="catalog-select"
      :aria-label="t('KANBAN.ADD_PRODUCT')"
      :disabled="disabled || isPending || hasError || !filteredProducts.length"
      class="w-full px-2.5 py-1.5 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 outline-none focus:ring-2 focus:ring-n-brand"
      @change="
        emit('addProduct', $event.target.value);
        $event.target.value = '';
      "
    >
      <option value="">{{ `+ ${t('KANBAN.ADD_PRODUCT')}` }}</option>
      <option
        v-for="product in filteredProducts"
        :key="product.id"
        :value="product.id"
      >
        {{ `${product.name} (${priceLabel(product)})` }}
      </option>
    </select>
  </div>
</template>
